import type {
  CardActionWorkflowResult,
  CardReadAction,
} from "@ascleo/doc-cirrus-bridge/schemas/frontdesk/cardreader/card-read-workflow-result.schema.ts";

type WorkflowState = Pick<
  CardActionWorkflowResult,
  "crlogId" | "status" | "possibleActions" | "validationStatus"
>;
interface PendingWorkflow {
  state: WorkflowState;
  action?: Promise<CardActionWorkflowResult>;
  abandonment?: Promise<void>;
  uncertain: boolean;
}

/**
 * Own pending card operations, serializing actions and cancellation without retaining demographics.
 * @since 1.1.10
 * @example
 * const lifecycle = new CardWorkflowLifecycle(cancelCardRead);
 */
export class CardWorkflowLifecycle {
  private readonly pending = new Map<string, PendingWorkflow>();
  private readonly cancel: (input: {
    crlogId: string;
  }) => Promise<CardActionWorkflowResult>;

  constructor(
    cancel: (input: {
      crlogId: string;
    }) => Promise<CardActionWorkflowResult>,
  ) {
    this.cancel = cancel;
  }

  /**
   * Remember only workflows which still require a decision.
   * @since 1.1.10
   * @example
   * lifecycle.remember({ crlogId: "read-1", status: "MERGED", possibleActions: ["apply", "cancel"] });
   */
  remember(state: WorkflowState): void {
    if (state.status === "APPLIED" || state.status === "CANCELLED") return;
    this.pending.set(state.crlogId, {
      state: {
        crlogId: state.crlogId,
        status: state.status,
        possibleActions: [...state.possibleActions],
        validationStatus: state.validationStatus,
      },
      uncertain: false,
    });
  }

  /**
   * Keep a new physical read behind cleanup of the previous screen's operation.
   * @since 1.1.10
   * @example
   * await lifecycle.waitForCleanup();
   */
  async waitForCleanup(): Promise<void> {
    for (const workflow of this.pending.values()) {
      if (!workflow.abandonment)
        throw new Error("A card workflow still requires a decision");
      await workflow.abandonment;
    }
  }

  /**
   * Execute one advertised action; an uncertain outcome must not be retried blindly.
   * @since 1.1.10
   * @example
   * await lifecycle.runAction("read-1", "apply", () => applyCardData({ crlogId: "read-1" }));
   */
  async runAction(
    crlogId: string,
    action: CardReadAction,
    operation: () => Promise<CardActionWorkflowResult>,
  ): Promise<CardActionWorkflowResult> {
    const workflow = this.pending.get(crlogId);
    if (
      !workflow ||
      workflow.action ||
      workflow.abandonment ||
      workflow.uncertain ||
      !workflow.state.possibleActions.includes(action)
    ) {
      throw new Error(
        "Card action is unavailable; return to reception if the outcome is unknown",
      );
    }
    const request = Promise.resolve()
      .then(operation)
      .then((result) => {
        if (result.crlogId !== crlogId)
          throw new Error("Card action returned a different operation");
        workflow.state = {
          crlogId: result.crlogId,
          status: result.status,
          possibleActions: [...result.possibleActions],
          validationStatus: result.validationStatus,
        };
        if (result.status === "APPLIED" || result.status === "CANCELLED")
          this.pending.delete(crlogId);
        return result;
      })
      .catch((error: unknown) => {
        workflow.uncertain = true;
        throw error;
      });
    workflow.action = request;
    try {
      return await request;
    } finally {
      workflow.action = undefined;
    }
  }

  /**
   * Cancel an abandoned pending workflow once, after any running action settles.
   * @since 1.1.10
   * @example
   * await lifecycle.abandon("read-1");
   */
  abandon(crlogId: string): Promise<void> {
    const workflow = this.pending.get(crlogId);
    if (!workflow) return Promise.resolve();
    if (workflow.abandonment) return workflow.abandonment;
    const cleanup = async () => {
      try {
        if (workflow.action) await workflow.action;
        if (workflow.uncertain)
          throw new Error(
            "Card action outcome is unknown; automatic cancellation was withheld",
          );
        if (
          workflow.state.status === "APPLIED" ||
          workflow.state.status === "CANCELLED"
        )
          return;
        if (!workflow.state.possibleActions.includes("cancel")) {
          throw new Error(
            "Pending card workflow cannot be cancelled through the advertised API",
          );
        }
        const result = await this.cancel({ crlogId });
        if (result.crlogId !== crlogId || result.status !== "CANCELLED") {
          throw new Error("Card cancellation was not confirmed");
        }
      } finally {
        this.pending.delete(crlogId);
      }
    };
    workflow.abandonment = cleanup();
    return workflow.abandonment;
  }
}
